import { db } from './db';
import { SeedData } from './reports/seedData';
import {
  CURRENT_DEV_DATA_VERSION,
  isLocalHost,
  isPreserveCustomDataEnabled,
  readStoredDevDataVersion,
} from './devDatabaseVersion';

type DevDataResetReason = 'missing-version' | 'version-mismatch';

interface EnsureDevDataFreshnessResult {
  resetTriggered: boolean;
  reason?: DevDataResetReason;
  preserveEnabled: boolean;
  skipped?: boolean;
  storedVersion?: string;
  expectedVersion: string;
}

const logger = typeof console !== 'undefined' ? console : undefined;

async function hasExistingData(): Promise<boolean> {
  const companyCount = await db.companies.count();
  if (companyCount > 0) {
    return true;
  }

  const reportCount = await db.reports.count();
  return reportCount > 0;
}

export async function ensureDevDataFreshness(): Promise<EnsureDevDataFreshnessResult> {
  const preserveEnabled = isPreserveCustomDataEnabled();
  const storedVersion = await readStoredDevDataVersion();
  const expectedVersion = CURRENT_DEV_DATA_VERSION;

  const shouldAutoReset =
    typeof window !== 'undefined' &&
    import.meta.env.DEV &&
    isLocalHost(window.location.hostname) &&
    !preserveEnabled;

  if (!storedVersion) {
    const dataPresent = await hasExistingData();

    if (!dataPresent) {
      // First run on a clean database: seeding logic will populate and stamp the version.
      return {
        resetTriggered: false,
        preserveEnabled,
        storedVersion,
        expectedVersion,
      };
    }

    if (!shouldAutoReset) {
      logger?.info?.(
        '[DevData] Missing stored version but auto-reset disabled (dev=%s, preserve=%s).',
        import.meta.env.DEV,
        preserveEnabled
      );
      return {
        resetTriggered: false,
        preserveEnabled,
        skipped: true,
        storedVersion,
        expectedVersion,
        reason: undefined,
      };
    }

    logger?.info?.('[DevData] Resetting dev data due to missing stored version.');
    await SeedData.clearAndSeed();
    return {
      resetTriggered: true,
      preserveEnabled,
      reason: 'missing-version',
      storedVersion,
      expectedVersion,
    };
  }

  if (storedVersion === expectedVersion) {
    return {
      resetTriggered: false,
      preserveEnabled,
      storedVersion,
      expectedVersion,
    };
  }

  if (!shouldAutoReset) {
    logger?.info?.(
      '[DevData] Stored version %s differs from expected %s but auto-reset skipped (dev=%s, preserve=%s).',
      storedVersion,
      expectedVersion,
      import.meta.env.DEV,
      preserveEnabled
    );
    return {
      resetTriggered: false,
      preserveEnabled,
      skipped: true,
      storedVersion,
      expectedVersion,
      reason: 'version-mismatch',
    };
  }

  logger?.info?.(
    '[DevData] Resetting dev data to align schema (stored=%s, expected=%s).',
    storedVersion,
    expectedVersion
  );

  await SeedData.clearAndSeed();

  return {
    resetTriggered: true,
    preserveEnabled,
    reason: 'version-mismatch',
    storedVersion,
    expectedVersion,
  };
}
